import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";

/**
 * Shared 1-second tick — single setInterval at the app root, broadcast to all
 * consumers via context. Before this consolidation each useNow() call
 * instantiated its own setInterval, which meant ~15 timers fired every second
 * across the tree (BridgeRow × lots, SiteCard × sites, RefreshIndicator,
 * OverallStatusBanner, ZoneList, etc). Now one timer feeds all of them.
 *
 * Usage:
 *   1. Wrap your tree once at the root:    <NowProvider>...children...</NowProvider>
 *   2. Anywhere downstream:                const now = useNow();
 *
 * Without a provider (tests, isolated story renders) useNow() returns a
 * one-shot Date.now() — no re-render, but no crash either.
 *
 * Implementation: useReducer counter forces a new context value each tick
 * because React bails out of identical state updates (and two Date.now()
 * calls inside a tight render pipeline can return the same ms).
 */

const NowContext = createContext<number | null>(null);

interface NowProviderProps {
  children: ReactNode;
  intervalMs?: number;
}

export function NowProvider({ children, intervalMs = 1000 }: NowProviderProps) {
  const [tick, bumpTick] = useReducer((x: number) => (x + 1) | 0, 0);

  useEffect(() => {
    const id = window.setInterval(bumpTick, intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);

  // Recompute Date.now() per tick so memoized children see a new reference.
  const value = useMemo(() => Date.now(), [tick]);

  return <NowContext.Provider value={value}>{children}</NowContext.Provider>;
}

export function useNow(): number {
  const ctx = useContext(NowContext);
  if (ctx !== null) return ctx;
  // Fallback: no provider — snapshot without subscribing to ticks.
  return Date.now();
}
